import React, {useState, useEffect, useRef} from "react";
import GoogleMapReact from "google-map-react";
import apiKey from "../../_core/apiKey";
import {useStore} from "hooks/useStore";
import {useTranslation} from "react-i18next";
import {useFetch} from "hooks/useFetch";
import {CustomerMapFilters} from "./customerMapFilters.js/CustomerMapFilters";
import {CourierMarker} from "./courierMarker/CourierMarker";
import {ArrowsAltOutlined, ShrinkOutlined, ShopOutlined} from "@ant-design/icons";
import {MapContainer, MapTools} from "./CustomerMap.styles";
import {PickupMarker} from "../dashboard/Marker/PickupMarker/PickupMarker";
import {pickupColor} from "../../constants/constants";

export const CustomerMap = () => {
    const [center, setCenter] = useState(null);
    const [zoom, setZoom] = useState(11);
    const [mapMode, setMapMode] = useState("relative");
    const stores = useStore();
    const {i18n} = useTranslation();
    const [recall, setRecall] = useState(true);
    const recallRef = useRef(recall);
    recallRef.current = recall;
    const [autoCenter, setAutoCenter] = useState(true);

    const [isDedicatedIntegrationLoading, isDedicatedIntegration] = useFetch({
        fetchFn: stores.poolStore.isDedicatedIntegration.bind(),
        dependencies: [],
        initialCall: true
    });

    const companyOptions = isDedicatedIntegration?.isIntegrationTypeDedicated?.companyIds || [];
    const subCompanyOptions =
        isDedicatedIntegration?.isIntegrationTypeDedicated?.subCompanyIds || [];
    const firstCompanyId = companyOptions[0]?._id;
    const firstSubCompanyId = subCompanyOptions[0]?._id;

    const [dedicatedCompanyIds, setDedicatedCompanyIds] = useState([]);
    const [dedicatedSubCompanyIds, setDedicatedSubCompanyIds] = useState([]);

    useEffect(() => {
        setDedicatedCompanyIds([firstCompanyId]);
        setDedicatedSubCompanyIds([firstSubCompanyId]);
    }, [isDedicatedIntegration]);

    const getValidCompanyIds = () => {
        if (dedicatedCompanyIds.length > 0 && dedicatedCompanyIds[0]) {
            return dedicatedCompanyIds;
        }
        return firstCompanyId ? [firstCompanyId] : [];
    };

    const getValidSubCompanyIds = () => {
        if (dedicatedSubCompanyIds.length > 0 && dedicatedSubCompanyIds[0]) {
            return dedicatedSubCompanyIds;
        }
        return firstSubCompanyId ? [firstSubCompanyId] : [];
    };

    const [
        customerMapListLoading,
        customerMapList,
        setCustomerMapList,
        setCustomerMapListLoading
    ] = useFetch({
        defaultDataValue: [],
        fetchFn: stores.courierStore.getCustomerMapList.bind(null, {
            dedicatedCompanyIds: getValidCompanyIds(),
            dedicatedSubCompanyIds: getValidSubCompanyIds()
        }),
        condition:
            !isDedicatedIntegrationLoading &&
            isDedicatedIntegration?.isIntegrationTypeDedicated &&
            getValidCompanyIds().length > 0 &&
            getValidSubCompanyIds().length > 0,
        dependencies: [dedicatedCompanyIds, dedicatedSubCompanyIds, recall, isDedicatedIntegration]
    });

    useEffect(() => {
        let timeout = null;
        timeout = setTimeout(() => {
            setRecall(!recallRef.current);
        }, 2000);
        return () => clearTimeout(timeout);
    }, [recall]);

    useEffect(() => {
        setAutoCenter(true);
    }, [dedicatedCompanyIds, dedicatedSubCompanyIds]);

    useEffect(() => {
        if (
            (customerMapList && customerMapList.length > 0) ||
            isDedicatedIntegration?.isIntegrationTypeDedicated?.subCompanyIds?.length > 0
        ) {
            if (autoCenter) {
                let allLocations = [];

                if (customerMapList && customerMapList.length > 0) {
                    allLocations = customerMapList.map(courier => ({
                        lat: courier.location.coordinates[1],
                        lng: courier.location.coordinates[0]
                    }));
                }

                if (isDedicatedIntegration?.isIntegrationTypeDedicated?.subCompanyIds) {
                    const subcompanyLocations = isDedicatedIntegration.isIntegrationTypeDedicated.subCompanyIds.map(
                        subcompany => ({
                            lat: subcompany.location.latitude,
                            lng: subcompany.location.longitude
                        })
                    );
                    allLocations = [...allLocations, ...subcompanyLocations];
                }

                if (allLocations.length > 0) {
                    const bounds = allLocations.reduce(
                        (acc, location) => {
                            return {
                                minLat: Math.min(acc.minLat, location.lat),
                                maxLat: Math.max(acc.maxLat, location.lat),
                                minLng: Math.min(acc.minLng, location.lng),
                                maxLng: Math.max(acc.maxLng, location.lng)
                            };
                        },
                        {
                            minLat: 90,
                            maxLat: -90,
                            minLng: 180,
                            maxLng: -180
                        }
                    );
                    const centerLat = (bounds.minLat + bounds.maxLat) / 2;
                    const centerLng = (bounds.minLng + bounds.maxLng) / 2;
                    setCenter({lat: centerLat, lng: centerLng});
                    const latDiff = bounds.maxLat - bounds.minLat;
                    const lngDiff = bounds.maxLng - bounds.minLng;
                    const maxDiff = Math.max(latDiff, lngDiff);
                    let newZoom = 11;
                    if (maxDiff > 0.5) newZoom = 8;
                    else if (maxDiff > 0.2) newZoom = 9;
                    else if (maxDiff > 0.1) newZoom = 10;
                    else if (maxDiff > 0.05) newZoom = 11;
                    else if (maxDiff > 0.02) newZoom = 12;
                    else newZoom = 13;
                    setZoom(newZoom);
                    setAutoCenter(false);
                } else {
                    setCenter({lat: 41.11, lng: 29.11});
                }
            }
        }
    }, [customerMapList, isDedicatedIntegration, autoCenter]);

    useEffect(() => {
        window.addEventListener("keydown", handleEsc);
        return () => {
            window.removeEventListener("keydown", handleEsc);
        };
    }, [mapMode]);

    const handleEsc = event => {
        if (event.keyCode === 27 && mapMode === "fixed") {
            setMapMode("relative");
        }
    };

    const toggleMapMode = () => {
        if (mapMode === "fixed") {
            setMapMode("relative");
        } else {
            setMapMode("fixed");
        }
    };

    return (
        <div style={{width: "100%"}}>
            <MapContainer mode={mapMode} data-testid="map" style={{width: "100%"}}>
                <CustomerMapFilters
                    dedicatedCompanyIds={dedicatedCompanyIds}
                    dedicatedSubCompanyIds={dedicatedSubCompanyIds}
                    setDedicatedCompanyIds={setDedicatedCompanyIds}
                    setDedicatedSubCompanyIds={setDedicatedSubCompanyIds}
                    companyOptions={companyOptions}
                    subCompanyOptions={subCompanyOptions}
                />
                <MapTools
                    onClick={e => {
                        toggleMapMode();
                        e.currentTarget.blur();
                    }}
                >
                    {mapMode === "relative" ? <ArrowsAltOutlined /> : <ShrinkOutlined />}
                </MapTools>
                <div
                    style={{
                        width: "100%",
                        height: "100%",
                        position: "relative",
                        flex: 1
                    }}
                >
                    <GoogleMapReact
                        style={{width: "100%", height: "100%"}}
                        center={center}
                        bootstrapURLKeys={{
                            key: apiKey,
                            language: i18n.resolvedLanguage || "tr"
                        }}
                        yesIWantToUseGoogleMapApiInternals
                        heatmapLibrary={true}
                        zoom={zoom}
                        options={{
                            fullscreenControl: false,
                            zoomControl: true,
                            gestureHandling: "greedy"
                        }}
                        onChange={e => {
                            setCenter(e.center);
                            setZoom(e.zoom);
                            setAutoCenter(false);
                        }}
                    >
                        {customerMapList &&
                            customerMapList.map((courier, index) => (
                                <CourierMarker
                                    key={courier._id || index}
                                    lat={courier.location.coordinates[1]}
                                    lng={courier.location.coordinates[0]}
                                    courier={courier}
                                />
                            ))}
                        {isDedicatedIntegration?.isIntegrationTypeDedicated?.subCompanyIds &&
                            isDedicatedIntegration.isIntegrationTypeDedicated.subCompanyIds.map(
                                (subcompany, index) => (
                                    <PickupMarker
                                        key={"subcompany-" + subcompany._id}
                                        icon={<ShopOutlined />}
                                        lat={subcompany.location.latitude}
                                        lng={subcompany.location.longitude}
                                        color={pickupColor}
                                        pickupInfo={{
                                            ...subcompany,
                                            companyName:
                                                isDedicatedIntegration.isIntegrationTypeDedicated.companyIds.find(
                                                    company => company._id === subcompany.companyId
                                                )?.companyName || subcompany.name
                                        }}
                                    />
                                )
                            )}
                    </GoogleMapReact>
                </div>
            </MapContainer>
        </div>
    );
};
